import React, { useMemo, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, RotateCcw, Home, Sparkles, Clock, AlertTriangle, PenTool } from "lucide-react";
import Sidebar from "@/components/wizard/Sidebar";
import Step1Agency from "@/components/steps/Step1Agency";
import Step2ADL from "@/components/steps/Step2ADL";
import Step3Nursing from "@/components/steps/Step3Nursing";
import Step4Comm from "@/components/steps/Step4Comm";
import Step5Medical from "@/components/steps/Step5Medical";
import Step6Social from "@/components/steps/Step6Social";
import Step7NSA from "@/components/steps/Step7NSA";
import Step7CMS485 from "@/components/steps/Step7CMS485";
import Step8AIReview from "@/components/steps/Step8AIReview";
import Step9Verify from "@/components/steps/Step9Verify";
import { STEPS } from "@/components/wizard/FormKit";
import { useWizard } from "@/context/WizardContext";
import { bootstrapAvidaDraft } from "@/lib/avidaDraft";
import RnMismatchBlock from "@/components/wizard/RnMismatchBlock";
import DraftAutoSaveBadge from "@/components/wizard/DraftAutoSaveBadge";
import DownloadAuditReceipt from "@/components/wizard/DownloadAuditReceipt";
import StepScratchpad from "@/components/wizard/StepScratchpad";
import DhsStepBanner from "@/components/wizard/DhsStepBanner";
import WizardGuidedTour, {
  isTourSeen as isGuidedTourSeen,
  markTourSeen as markGuidedTourSeen,
} from "@/components/wizard/WizardGuidedTour";
import { validateStep, focusMissing } from "@/lib/stepValidation";
import { findUnansweredOptionalGroups } from "@/lib/optionalGroups";
import { fieldToTestId } from "@/lib/fieldToStep";

const STEP_COMPONENTS = {
  1: Step1Agency, 2: Step2ADL, 3: Step3Nursing, 4: Step4Comm,
  5: Step5Medical, 6: Step6Social, 7: Step7NSA, 8: Step7CMS485,
  9: Step8AIReview, 10: Step9Verify,
};

// Rough per-step completion estimates (minutes). Feeds the progress ETA.
// Per-step time estimates in seconds — calibrated against actual home-visit
// observation. Lives here as a single source of truth that pairs with the
// `eta` strings on STEPS (see FormKit.jsx). Total ≈ 11 min, matching the tour
// "30-min visit → 12-min packet" promise. Update both together if revising.
const STEP_SECONDS = { 1: 60, 2: 90, 3: 90, 4: 30, 5: 90, 6: 60, 7: 90, 8: 60, 9: 30, 10: 60 };

function formatRemaining(totalSec) {
  if (totalSec < 60) return `~${totalSec}s remaining`;
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return s ? `~${m}m ${s}s remaining` : `~${m}m remaining`;
}

export default function Wizard() {
  const { step } = useParams();
  const navigate = useNavigate();
  const current = Math.min(10, Math.max(1, parseInt(step || "1", 10)));
  const StepView = STEP_COMPONENTS[current];
  const { data, reset, setData, loadAvidaDraft } = useWizard();
  const [rnBlock, setRnBlock] = useState(null);
  const [guidedTourOpen, setGuidedTourOpen] = useState(false);
  const [guidedSeen, setGuidedSeen] = useState(() => isGuidedTourSeen());

  // Avida Care deep-link bootstrap (Prompt 6B). Runs once per mount; if the
  // URL carries `?draft=<id>&source=avida_care`, fetches the seeded state,
  // merges it into context, fires /claim, and strips the URL params.
  // No-op otherwise — the normal wizard flow is unaffected. The Step 1
  // banner pulls state from WizardContext.avidaDraftId directly.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const draft = await bootstrapAvidaDraft({
        loadAvidaDraft,
        // Pass setRnBlock as a callback so StrictMode's double-mount +
        // URL-strip can't drop the signal. The first mount fires the
        // fetch, second mount finds no params; when the first response
        // resolves with 403, it calls onBlocked directly — bypassing
        // any `cancelled` check on the awaited path.
        onBlocked: (payload) => setRnBlock(payload),
      });
      if (cancelled || !draft) return;
      if (draft.__block && draft.code === "rn_mismatch") {
        // Redundant safety net for non-StrictMode paths (also covers
        // future callers that don't pass `onBlocked`).
        setRnBlock({
          assignedTo: draft.assignedTo,
          youAre: draft.youAre,
          message: draft.message,
        });
        return;
      }
      toast("Client record pre-loaded from Avida Care", {
        description: "Review each pre-filled field and correct anything that has changed.",
      });
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const meta = useMemo(() => STEPS.find((s) => s.id === current), [current]);
  const remainingSeconds = useMemo(() => {
    let s = 0;
    for (let i = current; i <= 10; i++) s += STEP_SECONDS[i] || 60;
    return s;
  }, [current]);

  // Confirm-skip modal state for fully-empty optional groups.
  const [skipConfirm, setSkipConfirm] = useState(null); // { groups: [...], onConfirm: () => void }

  // When the nurse jumps here from an AI-review "Fix in Step N" button, a
  // pending focus record is parked in sessionStorage. Read it after the
  // step's DOM has painted, then scroll + red-ring the offending field.
  useEffect(() => {
    let raw = null;
    try { raw = sessionStorage.getItem("wizard_focus_field"); } catch { /* non-fatal */ }
    if (!raw) return;
    let parsed;
    try { parsed = JSON.parse(raw); } catch { parsed = null; }
    if (!parsed || parsed.step !== current || !parsed.field) return;
    try { sessionStorage.removeItem("wizard_focus_field"); } catch { /* non-fatal */ }
    const testid = fieldToTestId(parsed.field);
    // Defer to next tick so the step component has mounted.
    const t = setTimeout(() => {
      focusMissing(testid);
      toast("Brought you back to this field — update and hit Next.", {
        description: parsed.field,
      });
    }, 220);
    return () => clearTimeout(t);
  }, [current]);

  // After all hooks have been declared, surface the hard-block screen
  // when the backend's `_enforce_assigned_rn` returned 403.
  if (rnBlock) {
    return (
      <RnMismatchBlock
        assignedTo={rnBlock.assignedTo}
        youAre={rnBlock.youAre}
        message={rnBlock.message}
      />
    );
  }

  const goto = (n) => navigate(`/wizard/${n}`);

  const handleNext = () => {
    const { ok, missing } = validateStep(current, data);
    if (!ok) {
      const first = missing[0];
      toast.error(
        `${missing.length === 1 ? "1 field" : `${missing.length} fields`} still needed — ${first.label}.`,
        { description: "Scrolled to it; I'll wait until this step is clean." }
      );
      focusMissing(first.testid);
      return;
    }
    // Hard-required fields are filled. Now check OPTIONAL groups that are
    // entirely empty — surface a confirm modal so the RN explicitly
    // acknowledges skipping vs forgetting.
    const empty = findUnansweredOptionalGroups(current, data);
    if (empty.length > 0) {
      setSkipConfirm({
        groups: empty,
        onConfirm: () => {
          // Record the acknowledged-skip in wizard state so it shows up in the
          // audit receipt and on the parent-app handoff payload.
          const stamped = empty.map((g) => ({
            step: current,
            group_id: g.id,
            group_label: g.label,
            acknowledged_at: new Date().toISOString(),
          }));
          setData((d) => {
            const prev = Array.isArray(d?._skipped_groups) ? d._skipped_groups : [];
            // Replace any existing entries for the same step+group so the
            // latest acknowledgement timestamp wins.
            const seen = new Set(stamped.map((s) => `${s.step}:${s.group_id}`));
            const kept = prev.filter((s) => !seen.has(`${s.step}:${s.group_id}`));
            return { ...d, _skipped_groups: [...kept, ...stamped] };
          });
          setSkipConfirm(null);
          goto(current + 1);
        },
      });
      return;
    }
    goto(current + 1);
  };

  return (
    <div className="flex min-h-screen">
      <Sidebar currentStep={current} />

      <main className="flex-1 min-w-0" data-testid="wizard-main">
        <div className="lg:hidden sticky top-0 z-20 bg-[#F7FAFC]/92 backdrop-blur border-b border-[#D6E3EF] px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-[#0B1E3A]" data-testid="wizard-home-mobile">
            <Home className="w-5 h-5" />
            <span className="font-display text-[14px] font-semibold">Wisconsin PA</span>
          </Link>
          <div className="text-[12px] text-[#3A5275]">Step {current}/10</div>
        </div>

        <div className="max-w-[960px] px-5 md:px-10 py-8 lg:py-12 mx-auto">
          <div className="mb-8 flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-[#3A5275] mb-2 inline-flex items-center gap-2 flex-wrap">
                Step {current} of 10 · {meta?.title}
                <span
                  className="inline-flex items-center gap-1 px-2 h-5 rounded-full bg-[#FFFDEC] border border-[#DBB90A] text-[#8A6B00] normal-case tracking-normal font-semibold text-[11px]"
                  data-testid="wizard-step-time"
                  title={meta?.etaHint || ""}
                >
                  <Clock className="w-3 h-3" /> {meta?.eta || "≈ 60 sec"} for this step
                </span>
                <span className="inline-flex items-center gap-1 text-[#3A5275] normal-case tracking-normal font-medium" data-testid="wizard-remaining-time">
                  <Sparkles className="w-3 h-3" /> {formatRemaining(remainingSeconds)}
                </span>
              </div>
              <h1 className="font-display text-3xl md:text-4xl font-semibold text-[#0B1E3A] leading-tight tracking-tight">
                {meta?.title}
              </h1>
              <p className="text-[#3A5275] mt-2 max-w-xl">{meta?.blurb}</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <DraftAutoSaveBadge />
              <DownloadAuditReceipt label="My responses" />
              <button
                onClick={() => { try { localStorage.removeItem("wi-pa-tour-seen-v1"); } catch { /* localStorage unavailable — non-fatal */ } window.location.reload(); }}
                className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full text-[11.5px] font-semibold bg-[#FFFDEC] border border-[#DBB90A] text-[#8A6B00] hover:bg-[#fdf3de]"
                data-testid="wizard-show-tour"
                title="Replay the 5-stage welcome tour"
              >
                <Sparkles className="w-3 h-3" /> Tour
              </button>
              <button
                onClick={() => { setGuidedTourOpen(true); markGuidedTourSeen(); setGuidedSeen(true); }}
                className={`inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full text-[11.5px] font-semibold bg-[#F3E8FF] border border-[#9333EA] text-[#6B21A8] hover:bg-[#EDE0FF] ${
                  !guidedSeen ? "ring-2 ring-[#9333EA]/40 ring-offset-1 animate-pulse" : ""
                }`}
                data-testid="wizard-show-guided-tour"
                title="Interactive walkthrough — spotlight on every key feature"
              >
                <PenTool className="w-3 h-3" /> Guided
                {!guidedSeen && (
                  <span
                    className="w-1.5 h-1.5 rounded-full bg-[#9333EA]"
                    data-testid="wizard-guided-tour-new-dot"
                  />
                )}
              </button>
              <button onClick={() => { reset(); navigate("/wizard/1"); toast("Wizard reset. Fresh intake ready."); }}
                className="btn-ghost" data-testid="wizard-reset">
                <RotateCcw className="w-4 h-4" /> Reset
              </button>
            </div>
          </div>

          <div className="mb-8 h-1.5 bg-[#D6E3EF] rounded-full overflow-hidden" data-testid="wizard-progress">
            <div
              className="h-full bg-gradient-to-r from-[#0A3D79] via-[#3a5b4d] to-[#a07a27] transition-all"
              style={{ width: `${(current / 10) * 100}%` }}
            />
          </div>

          <div className="fade-up">
            <DhsStepBanner step={current} />
            <StepView />
          </div>

          <StepScratchpad stepId={current} />

          <div className="mt-10 pt-6 border-t border-[#D6E3EF] flex items-center justify-between gap-3 flex-wrap">
            <button type="button" disabled={current === 1} onClick={() => goto(current - 1)} className="btn-ghost" data-testid="wizard-prev">
              <ArrowLeft className="w-4 h-4" /> Previous
            </button>
            <div className="text-[12.5px] text-[#3A5275]">
              Saved locally · {current}/10
            </div>
            {current < 10 ? (
              <button type="button" onClick={handleNext} className="btn-primary" data-testid="wizard-next">
                Continue <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <div className="text-[13px] text-[#3A5275] italic">Complete verification to generate PDFs.</div>
            )}
          </div>
        </div>
      </main>
      {skipConfirm && (
        <div
          className="fixed inset-0 z-[120] bg-[#0B1E3A]/60 backdrop-blur-sm flex items-center justify-center px-4"
          data-testid="skip-confirm-modal"
        >
          <div className="bg-white rounded-2xl shadow-2xl border border-[#D6E3EF] max-w-md w-full overflow-hidden">
            <div className="px-6 pt-6 pb-3 border-b border-[#E7EEF5]">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-full bg-[#FFFDEC] border border-[#DBB90A] flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-4 h-4 text-[#8A6B00]" />
                </div>
                <div>
                  <h3 className="font-display text-[18px] font-semibold text-[#0B1E3A]">
                    Skipping optional sections?
                  </h3>
                  <div className="text-[12.5px] text-[#3A5275]">
                    No required fields are missing — but these are entirely empty.
                  </div>
                </div>
              </div>
            </div>
            <div className="px-6 py-4">
              <ul className="space-y-1.5" data-testid="skip-confirm-list">
                {skipConfirm.groups.map((g) => (
                  <li
                    key={g.id}
                    className="flex items-center gap-2 text-[13.5px] text-[#0B1E3A]"
                    data-testid={`skip-group-${g.id}`}
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-[#DBB90A]" />
                    {g.label}
                  </li>
                ))}
              </ul>
              <p className="text-[12.5px] text-[#3A5275] mt-4 leading-relaxed">
                Confirming will record your acknowledgement in the audit trail.
                Cancel if you want to go back and fill them in.
              </p>
            </div>
            <div className="px-6 py-4 bg-[#F7FAFC] border-t border-[#E7EEF5] flex justify-end gap-2">
              <button
                onClick={() => setSkipConfirm(null)}
                className="btn-ghost"
                data-testid="skip-cancel"
              >
                Go back &amp; fill
              </button>
              <button
                onClick={skipConfirm.onConfirm}
                className="btn-primary"
                data-testid="skip-confirm"
              >
                Acknowledge &amp; continue
              </button>
            </div>
          </div>
        </div>
      )}
      <WizardGuidedTour
        open={guidedTourOpen}
        onClose={() => setGuidedTourOpen(false)}
      />
    </div>
  );
}
